import widgetService from '@/services/widgetService';

const WIDGET_TYPE = 'billingProfile';

const state = {
    profiles: [],
    loading: false,
    error: null,
};

const mutations = {
    SET_PROFILES(state, profiles) {
        state.profiles = profiles;
    },
    ADD_PROFILE(state, profile) {
        state.profiles.push(profile);
    },
    UPDATE_PROFILE(state, updatedProfile) {
        const index = state.profiles.findIndex((p) => p.uuid === updatedProfile.uuid);
        if (index !== -1) {
            state.profiles.splice(index, 1, updatedProfile);
        }
    },
    REMOVE_PROFILE(state, profileId) {
        state.profiles = state.profiles.filter((p) => p.uuid !== profileId);
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    async fetchProfiles({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const profiles = await widgetService.getWidgetsByType(WIDGET_TYPE);
            commit('SET_PROFILES', profiles || []);
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch billing profiles:', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createProfile({ commit }, profileData) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const widgetData = {
                name: profileData.name,
                type: WIDGET_TYPE,
                state: profileData.state,
            };
            const newProfile = await widgetService.createWidget(widgetData);
            commit('ADD_PROFILE', newProfile);
            return newProfile;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to create billing profile:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updateProfile({ commit, getters }, { uuid, profileData }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const updateData = {};
            if (profileData.name) updateData.name = profileData.name;
            if (profileData.state) {
                const existingProfile = getters.getProfileById(uuid);
                const currentState = existingProfile?.state || {};
                updateData.state = {
                    ...currentState,
                    ...profileData.state,
                };
            }

            const updatedProfile = await widgetService.updateWidget(uuid, updateData);
            commit('UPDATE_PROFILE', updatedProfile);
            return updatedProfile;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to update billing profile:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async deleteProfile({ commit }, uuid) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            await widgetService.deleteWidget(uuid);
            commit('REMOVE_PROFILE', uuid);
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to delete billing profile:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async saveDriverOverride({ commit, getters, dispatch }, { profileId, driverId, blocks }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const profile = getters.getProfileById(profileId);
            if (!profile) throw new Error('Profile not found');

            const currentState = profile.state || {};
            const driverOverrides = currentState.driverOverrides || {};

            const driverIdKey = String(driverId);

            const updatedOverrides = {
                ...driverOverrides,
                [driverIdKey]: blocks,
            };

            const newState = {
                ...currentState,
                driverOverrides: updatedOverrides,
            };

            await dispatch('updateProfile', {
                uuid: profileId,
                profileData: { state: newState },
            });
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to save driver override:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },
};

const getters = {
    allProfiles: (state) => state.profiles,
    getProfileById: (state) => (id) => state.profiles.find((p) => p.uuid === id),
    isLoading: (state) => state.loading,
    error: (state) => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
